import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MoveLeft, MoveRight } from 'lucide-react';

import { useGetProductCatalogQuery } from '@/api/hooks/queries/use-get-product-catalog-query';
import { ShieldCheck } from '@/assets/icons/ShieldCheck.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useEffectiveCategoryLabels } from '@/hooks/use-effective-category-labels';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useRunOnce } from '@/hooks/use-run-once';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackEligibilityCheckpoint } from '@/lib/analytics';
import {
  filterProductsToSelectedCategoryLabels,
  partitionProductsByBenefitsCoverage,
} from '@/lib/benefits-product-coverage.ts';
import { SUPPORT_PHONE_E164 } from '@/lib/constants';
import { logger } from '@/lib/logger.ts';
import { getBaseInsuranceEligibilityEventAttributes } from '@/types/insurance-builder.type.ts';
import { prettyTypeName } from '@/types/insurance.type';
import BenefitsListing from '@/views/insurance/components/BenefitsListing.tsx';
import { EmptyBenefitsState } from '@/views/insurance/components/EmptyBenefitsState.tsx';

const InsuranceBenefitsHeader = ({
  anyCoveragePending,
  hasNoCoverage,
}: {
  anyCoveragePending: boolean;
  hasNoCoverage: boolean;
}) => {
  if (hasNoCoverage) {
    return (
      <CardTitle className="flex flex-col items-center justify-center gap-3.5">
        <span>Sorry, the supplies you requested require active insurance coverage.</span>
      </CardTitle>
    );
  }

  if (anyCoveragePending) {
    return (
      <CardTitle className="flex flex-col items-center justify-center gap-3.5">
        <span>Estimated benefits</span>
        <span>(pending coverage)</span>
      </CardTitle>
    );
  }

  return (
    <CardTitle className="flex flex-col items-center justify-center gap-3.5">
      <ShieldCheck className="size-10" />
      <span>Benefits results</span>
    </CardTitle>
  );
};

export const InsuranceBenefits = () => {
  const navigate = useNavigate();
  const { data: allProducts = [], isFetched: productCatalogFetched } = useGetProductCatalogQuery();
  const { insuranceBuilder, patientRecipient } = useSessionStore();
  const effectiveCategoryLabels = useEffectiveCategoryLabels();

  const scopedProducts = useMemo(
    () => filterProductsToSelectedCategoryLabels(allProducts, effectiveCategoryLabels),
    [allProducts, effectiveCategoryLabels]
  );

  const {
    coveredProducts,
    pendingProducts,
    notCoveredProducts,
    coveredCarrierTypes,
    notCoveredCarrierTypes,
    pendingCarrierTypes,
  } = useMemo(
    () => partitionProductsByBenefitsCoverage(scopedProducts, insuranceBuilder),
    [scopedProducts, insuranceBuilder]
  );
  const anyCoveragePending = insuranceBuilder.confirmedInsurances.length > 0;
  const anyCoverageUnprovided = insuranceBuilder.unprovidedInsurances.length > 0;

  const needsAdditionalCoverage = notCoveredCarrierTypes.length > 0;
  const hasListedProducts =
    coveredProducts.length > 0 || pendingProducts.length > 0 || notCoveredProducts.length > 0;
  const showEmptyBenefitsState = productCatalogFetched && !hasListedProducts;
  const hasCoveredProducts = coveredProducts.length > 0 || pendingProducts.length > 0;
  const hasNoCoverage = !hasCoveredProducts && notCoveredProducts.length > 0;
  const hasLoggedEmptyBenefitsState = useRef(false);

  useRunOnce(() => {
    const state = useSessionStore.getState();
    const attributes = getBaseInsuranceEligibilityEventAttributes(
      state.insuranceBuilder,
      state.insuranceZipCode
    );

    trackEligibilityCheckpoint({
      ...attributes,
    });
  });

  useEffect(() => {
    if (!showEmptyBenefitsState || hasLoggedEmptyBenefitsState.current) return;

    hasLoggedEmptyBenefitsState.current = true;
    logger.error(new Error('Insurance benefits has no products to display'), {
      selectedCategoryLabels: effectiveCategoryLabels,
      productCatalogCount: allProducts.length,
      scopedProductCount: scopedProducts.length,
      verifiedCarrierTypes: [...coveredCarrierTypes],
      confirmedCarrierTypes: [...pendingCarrierTypes],
      notCoveredCarrierTypes,
      unprovidedCarrierTypes: insuranceBuilder.unprovidedInsurances.map(
        (insurance) => insurance.carrierType
      ),
      carrierTypes: insuranceBuilder.carrierTypes,
      preDiscoveryCarrierTypes: insuranceBuilder.preDiscoveryCarrierTypes,
    });
  }, [
    allProducts.length,
    coveredCarrierTypes,
    effectiveCategoryLabels,
    insuranceBuilder.carrierTypes,
    insuranceBuilder.preDiscoveryCarrierTypes,
    insuranceBuilder.unprovidedInsurances,
    notCoveredCarrierTypes,
    pendingCarrierTypes,
    scopedProducts.length,
    showEmptyBenefitsState,
  ]);

  const { nextStep } = useProductSelectionStep();

  const handleRequestSupplies = () => {
    if (anyCoverageUnprovided) {
      return navigate({ to: nextStep });
    }

    if (patientRecipient === 'me') {
      return navigate({ to: '/create-profile/for-me' });
    } else {
      return navigate({ to: '/create-profile/for-someone-else' });
    }
  };

  const handleAddInsurance = () => {
    navigate({ to: '/insurance/input' });
  };

  const handleChooseDifferentSupplies = () => {
    navigate({ to: '/0' });
  };

  const toInsuranceFinalize = () => navigate({ to: '/insurance/finalize' });

  return (
    <section>
      <NavigationBar onBack={toInsuranceFinalize} backLabel="Insurance" stepIndicator="4 of 4" />
      <StepperCard currentStep={4} totalSteps={4}>
        <CardHeader>
          <InsuranceBenefitsHeader
            anyCoveragePending={anyCoveragePending}
            hasNoCoverage={hasNoCoverage}
          />
          <CardDescription className="mt-1 justify-self-center border-l-0 text-sm leading-[20px] text-charcoal-400">
            Note: Final coverage for these supplies is determined by a clinical assessment.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <BenefitsListing
            coveredCarrierTypes={coveredCarrierTypes}
            pendingCarrierTypes={pendingCarrierTypes}
            notCoveredCarrierTypes={notCoveredCarrierTypes}
            coveredProducts={coveredProducts}
            pendingProducts={pendingProducts}
            notCoveredProducts={notCoveredProducts}
          />

          {showEmptyBenefitsState ? (
            <EmptyBenefitsState onChooseDifferentSupplies={handleChooseDifferentSupplies} />
          ) : null}

          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            {needsAdditionalCoverage ? (
              <Button
                onClick={handleAddInsurance}
                className="w-full sm:mt-6 sm:w-auto sm:self-center"
                variant="outline"
              >
                <div className="flex items-center gap-2">
                  <MoveLeft className="size-6" />
                  Add {notCoveredCarrierTypes.map((carrier) => prettyTypeName(carrier)).join(' & ')}
                </div>
              </Button>
            ) : null}

            {hasCoveredProducts ? (
              <Button
                onClick={handleRequestSupplies}
                className="w-full sm:mt-6 sm:w-auto sm:self-center"
              >
                <div className="flex items-center gap-2">
                  Request covered supplies
                  <MoveRight className="size-6" />
                </div>
              </Button>
            ) : null}
          </div>

          <p className="text-center text-sm leading-snug text-charcoal-500">
            Need help adding your plan?{' '}
            <a
              href={`tel:${SUPPORT_PHONE_E164}`}
              className="font-medium text-hydrangea-500 underline underline-offset-2"
            >
              Give us a call
            </a>
          </p>
        </CardContent>
      </StepperCard>
    </section>
  );
};
